/* eslint-disable no-console */
// Logger de requisições HTTP — só ativa em desenvolvimento.
// Mostra: método, URL, duração, status, agrupado por página/rota.
// Alerta: chamadas duplicadas (mesma URL em <2s) e bursts (>5 in-flight).

const isDev = process.env.NODE_ENV === 'development';

const state = {
  page: typeof window !== 'undefined' ? window.location.pathname : '/',
  pageStart: Date.now(),
  inFlight: new Map(), // requestId -> { url, method, start }
  pageCalls: [], // { url, method, status, duration, start } da página atual
  recentByUrl: new Map(), // url -> [timestamps]
  burstWarned: false,
};

const COLORS = {
  pending: 'color: #888',
  ok: 'color: #2c9c4a',
  slow: 'color: #d2932b; font-weight: bold',
  error: 'color: #c0392b; font-weight: bold',
  dup: 'color: #c0392b; font-weight: bold',
  burst: 'color: #c0392b; font-weight: bold; background: #ffeaa7',
  page: 'color: #2980b9; font-weight: bold',
};

const SLOW_MS = 1000;
const DUP_WINDOW_MS = 2000;
const BURST_THRESHOLD = 5;

// Endpoints conhecidos por retornar 404 ate o backend implementa-los.
// Frontend ja faz fallback .catch(() => ({ data: [] })) silencioso —
// aqui evitamos poluir o console com '[REQ] ... ERR' em cada chamada.
// Quando o backend implementar, basta remover daqui.
const EXPECTED_404_PATTERNS = [
  /\/api\/v1\/thing\/\d+\/type\/(trip|block)$/,
  /\/api\/v1\/thing\/efficiency\/\d+$/,
];

const isExpected404 = (url, status) => {
  if (status !== 404) return false;
  const cleaned = cleanUrl(url);
  return EXPECTED_404_PATTERNS.some((rx) => rx.test(cleaned));
};

const fmtMs = (ms) => `${ms.toFixed(0)}ms`;

const cleanUrl = (url = '') => {
  // remove query string para identificar duplicatas
  const [path] = url.split('?');
  return path;
};

const checkPageChange = () => {
  if (typeof window === 'undefined') return;
  const current = window.location.pathname;
  if (current !== state.page) {
    flushPageSummary();
    state.page = current;
    state.pageStart = Date.now();
    state.pageCalls = [];
    state.burstWarned = false;
  }
};

const flushPageSummary = () => {
  if (!state.pageCalls.length) return;

  const totalDuration = Date.now() - state.pageStart;
  const totalCalls = state.pageCalls.length;
  const slow = state.pageCalls.filter((c) => c.duration > SLOW_MS);

  // agrupa por URL
  const byUrl = state.pageCalls.reduce((acc, call) => {
    const key = `${call.method} ${cleanUrl(call.url)}`;
    if (!acc[key]) acc[key] = { count: 0, totalMs: 0, maxMs: 0, errors: 0 };
    acc[key].count += 1;
    acc[key].totalMs += call.duration;
    acc[key].maxMs = Math.max(acc[key].maxMs, call.duration);
    if (call.status >= 400 || call.status === 0) acc[key].errors += 1;
    return acc;
  }, {});

  console.groupCollapsed(
    `%c[REQ] Resumo de "${state.page}" — ${totalCalls} chamadas em ${fmtMs(totalDuration)}${
      slow.length ? ` (${slow.length} lentas)` : ''
    }`,
    COLORS.page
  );
  console.table(
    Object.entries(byUrl).map(([endpoint, s]) => ({
      endpoint,
      calls: s.count,
      total: fmtMs(s.totalMs),
      max: fmtMs(s.maxMs),
      avg: fmtMs(s.totalMs / s.count),
      errors: s.errors,
      duplicated: s.count > 1 ? '⚠️' : '',
    }))
  );
  console.groupEnd();
};

const recordIncoming = (url, method) => {
  checkPageChange();

  const id = Symbol('reqId');
  const start = performance.now();
  state.inFlight.set(id, { url, method, start });

  // duplicata?
  const cleaned = cleanUrl(url);
  const now = Date.now();
  const recent = (state.recentByUrl.get(cleaned) || []).filter(
    (t) => now - t < DUP_WINDOW_MS
  );
  recent.push(now);
  state.recentByUrl.set(cleaned, recent);

  if (recent.length > 1) {
    console.warn(
      `%c[REQ] Duplicata: ${method.toUpperCase()} ${cleaned} — ${recent.length}× em ${DUP_WINDOW_MS}ms`,
      COLORS.dup
    );
  }

  // burst?
  if (state.inFlight.size > BURST_THRESHOLD && !state.burstWarned) {
    console.warn(
      `%c[REQ] Burst: ${state.inFlight.size} requisições in-flight simultâneas em "${state.page}"`,
      COLORS.burst
    );
    state.burstWarned = true;
  }

  return id;
};

const recordCompleted = (id, status, error) => {
  const entry = state.inFlight.get(id);
  if (!entry) return;
  state.inFlight.delete(id);

  const duration = performance.now() - entry.start;
  const finalStatus = status || (error ? 0 : 200);

  // 404 esperados (endpoints nao implementados): nao polui pageCalls com
  // 'erro' (afeta o resumo final) nem loga linha vermelha. Frontend ja
  // tem fallback silencioso (.catch que retorna []), o operador nao
  // precisa ver ERR.
  if (isExpected404(entry.url, finalStatus)) {
    return;
  }

  state.pageCalls.push({
    url: entry.url,
    method: entry.method,
    status: finalStatus,
    duration,
    start: entry.start,
  });

  let style = COLORS.ok;
  let label = `${finalStatus}`;
  if (finalStatus >= 400 || finalStatus === 0) {
    style = COLORS.error;
    label = error ? 'ERR' : `${finalStatus}`;
  } else if (duration > SLOW_MS) {
    style = COLORS.slow;
  }

  console.log(
    `%c[REQ] ${entry.method.toUpperCase()} ${cleanUrl(entry.url)} — ${label} em ${fmtMs(
      duration
    )}`,
    style
  );
};

export const attachRequestLogger = (axios) => {
  if (!isDev || !axios || axios.hasReqLogger) return;
  axios.hasReqLogger = true;

  axios.interceptors.request.use(
    (config) => {
      const reqId = recordIncoming(config.url, config.method || 'get');
      config.metadata = { reqId };
      return config;
    },
    (err) => Promise.reject(err)
  );

  axios.interceptors.response.use(
    (response) => {
      const reqId = response.config && response.config.metadata && response.config.metadata.reqId;
      recordCompleted(reqId, response.status);
      return response;
    },
    (error) => {
      const reqId =
        error.config && error.config.metadata && error.config.metadata.reqId;
      const status = error.response && error.response.status;
      recordCompleted(reqId, status, error);
      return Promise.reject(error);
    }
  );

  // Periodicamente confere se trocou de página (caso roteador não dispare manualmente).
  if (typeof window !== 'undefined') {
    setInterval(checkPageChange, 1500);
    window.addEventListener('beforeunload', flushPageSummary);
  }

  console.log(
    `%c[REQ] Logger de requisições ativo. Página inicial: ${state.page}`,
    COLORS.page
  );
};
